import { type ReactNode } from "react"
import { Navigate, useLocation } from "react-router-dom"

import { getAccessToken } from "../../components/callback/callBack.service"
import { routeGuard } from "../../config/route-guards"
import { useIsScoped } from "../../hooks/useEntityScope"
import { storeIntendedUrl } from "../../services/intended-navigation"

interface PrivateRouteProps {
  children: ReactNode
}

/**
 * Enforces the route-guard table (DV-3012, spec §5.2 / D10).
 *
 * Takes no policy props. It used to accept `loginCheck` and `orgCheck`
 * booleans, each inverting to mean something different when false — and
 * `bootstrapAuth` declared the same auth question a third time in its
 * `publicRoutes` array. All three now read `routeGuard(path)`, so the guard
 * and the bootstrap are structurally incapable of disagreeing about a route.
 *
 * `entity: 'required'` checks the scope slice's `status`, not the token claim:
 * the claim says WHICH entity, `status` says one was CHOSEN in this tab, and
 * `initiateOrgSwitch()` clears only the latter to force the picker (DV-3012
 * §3.7 / D4). DV-3013 discharged that phase's promise — the marker is reactive
 * state now, so a switch or a clear reaches this guard without a reload.
 */
function PrivateRoute({ children }: PrivateRouteProps) {
  const { pathname } = useLocation()
  const guard = routeGuard(pathname)

  const isSignedIn = !!getAccessToken()
  const isScoped = useIsScoped()

  const authOk =
    guard.auth === "any" ||
    (guard.auth === "required" ? isSignedIn : !isSignedIn)

  const entityOk =
    guard.entity === "any" ||
    (guard.entity === "required" ? isScoped : !isScoped)

  if (authOk && entityOk) return children

  storeIntendedUrl()
  return <Navigate to="/" />
}

export default PrivateRoute
